import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {CreateSubscriptionOfferingForm} from "../../../../../components/offering-form/subscriptions";
import {FormWrapper} from "../../../../../components/form-wrapper";
import {useCreateSubscriptionContext} from "../../context/create-subscription";
import {
  CreateSubscriptionFormValues,
  createSubscriptionBasicFormValidationSchema,
} from "../../schema-validation";
import {useCreateSubscriptionHook} from "../../hooks/create-subscription";
import {restoreSubscriptionTierApiIdsFromDraft} from "../../create-subscription-service";

export function CreateSubscriptionBasicFormStep() {
  const navigate = useNavigate();
  const {
    dispatch,
    state: {formData},
  } = useCreateSubscriptionContext();
  const {createSubscription, createSubscriptionError, isCreatingSubscription} =
    useCreateSubscriptionHook();

  const formMethods = useForm<CreateSubscriptionFormValues>({
    resolver: yupResolver(createSubscriptionBasicFormValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      ...formData,
      description: formData.description ?? "",
    },
  });

  function onSubmit(values: CreateSubscriptionFormValues) {
    const tiersWithIds = restoreSubscriptionTierApiIdsFromDraft(
      values.tiers,
      formData.tiers
    );
    const payload: CreateSubscriptionFormValues = {
      ...values,
      description: values.description ?? "",
      tiers: tiersWithIds,
    };
    dispatch({
      type: "save-create-subscription-form-values",
      payload,
    });
    createSubscription(payload);
  }

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name || formMethods.formState.errors.description;
  const isTiersError = !!formMethods.formState.errors.tiers;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.url_redirect;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
        <Space direction="vertical" size={16} style={{display: "flex"}}>
          <FormWrapper borderless>
            <CreateSubscriptionOfferingForm
              isGeneralInformationError={!!isGeneralInformationError}
              isTiersError={isTiersError}
              isSettingsError={!!isSettingsError}
            />
          </FormWrapper>
          {isFormError && (
            <Alert
              message="Please fill in the required fields above, outlined in red"
              type="warning"
            />
          )}
          {createSubscriptionError && (
            <Alert
              message="Could not create subscription"
              description={createSubscriptionError}
              type="error"
              showIcon
            />
          )}
          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  onClick={() => {
                    dispatch({type: "reset-state"});
                    navigate("/products?activeTab=subscriptions");
                  }}
                  block
                  size="small"
                  type="default"
                >
                  Back
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="small"
                  disabled={isFormError}
                  loading={isCreatingSubscription}
                >
                  Next
                </Button>
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
    </FormProvider>
  );
}
